import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import { setMailPassword } from '../utils/mailCredentials';
import './ChangePassword.css';

const PASSWORD_STRONG_REGEX = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

const PasswordField = ({
  id,
  label,
  value,
  onChange,
  show,
  onToggleShow,
  autoFocus = false,
  disabled = false,
}) => (
  <div className="cp-field">
    <label htmlFor={id}>{label}</label>
    <input
      id={id}
      type={show ? 'text' : 'password'}
      value={value}
      onChange={onChange}
      autoFocus={autoFocus}
      disabled={disabled}
      autoComplete="new-password"
    />
    <button
      type="button"
      className="cp-toggle-visibility"
      onClick={onToggleShow}
      aria-label={show ? 'Hide password' : 'Show password'}
      tabIndex={-1}
    >
        {show ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 6.5c3.79 0 7.17 2.46 8.33 6-1.16 3.54-4.54 6-8.33 6s-7.17-2.46-8.33-6c1.16-3.54 4.54-6 8.33-6m0-2C6.48 4.5 2.73 7.61 1 12c1.73 4.39 5.48 7.5 11 7.5s9.27-3.11 11-7.5C20.27 7.61 16.52 4.5 12 4.5zm0 5c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 2c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z" />
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 6.5c3.79 0 7.17 2.46 8.33 6-1.16 3.54-4.54 6-8.33 6s-7.17-2.46-8.33-6c1.16-3.54 4.54-6 8.33-6m0-2C6.48 4.5 2.73 7.61 1 12c1.73 4.39 5.48 7.5 11 7.5s9.27-3.11 11-7.5C20.27 7.61 16.52 4.5 12 4.5zm0 5c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm-2 3c0 .55.45 1 1 1s1-.45 1-1-.45-1-1-1-1 .45-1 1z" />
          </svg>
        )}
    </button>
  </div>
);

const ChangePassword = () => {
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!PASSWORD_STRONG_REGEX.test(newPassword)) {
      setMessage({
        type: 'error',
        text: 'Use at least 8 characters with letters, numbers, and symbols',
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }

    setLoading(true);

    try {
      const response = await axiosInstance.post('/api/auth/change-password', {
        newPassword,
        confirmPassword,
      });

      if (response.data.success) {
        setMailPassword(newPassword);
        setMessage({ type: 'success', text: 'Password changed successfully' });
        setNewPassword('');
        setConfirmPassword('');
        setTimeout(() => navigate('/account'), 1200);
      } else {
        setMessage({
          type: 'error',
          text: response.data.message || 'Could not change password',
        });
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.message || 'Could not change password',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="change-password-page">
      <div className="change-password-inner">
        <header className="change-password-header">
          <button
            type="button"
            className="change-password-back"
            onClick={() => navigate('/account')}
          >
            ← Back to account
          </button>
          <h1>Change password</h1>
        </header>

        <p className="change-password-intro">
          Choose a strong password and don&apos;t reuse it for other accounts.
        </p>
        <p className="change-password-intro">
          Your mailbox (IMAP/SMTP) password will be updated to match.
        </p>

        <form className="change-password-form" onSubmit={handleSubmit}>
          <div className="change-password-card">
            <PasswordField
              id="newPassword"
              label="New password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              show={showNew}
              onToggleShow={() => setShowNew((v) => !v)}
              autoFocus
              disabled={loading}
            />
            <p className="change-password-hint">
              Password strength: Use at least 8 characters. Include letters, numbers,
              and symbols.
            </p>
            <PasswordField
              id="confirmPassword"
              label="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              show={showConfirm}
              onToggleShow={() => setShowConfirm((v) => !v)}
              disabled={loading}
            />
          </div>

          {message.text && (
            <p className={`change-password-message ${message.type}`} role="alert">
              {message.text}
            </p>
          )}

          <div className="change-password-actions">
            <button
              type="submit"
              className="change-password-submit"
              disabled={loading}
            >
              {loading ? 'Please wait...' : 'Change password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;
